import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useSendChatMessage } from "../../hooks/useSendChatMessage";
import { useEnduserStore } from "../../store/hooks";

interface UseNewConversationDialogProps {
  onOpenChange: (open: boolean) => void;
  onConversationCreated: () => void;
}

export const useNewConversationDialog = ({ onOpenChange, onConversationCreated }: UseNewConversationDialogProps) => {
  const { t } = useTranslation("messages");
  const session = useEnduserStore((state) => state.session);
  const enduserId = useEnduserStore((state) => state.currentEnduser?.id);

  const [subject, setSubject] = useState("");
  const [initialMessage, setInitialMessage] = useState("");

  const createRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "createOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });

  const { sendMessageAsync, isPending: isSendingMessage } = useSendChatMessage();

  const isPending = createRoomMutation.isPending || isSendingMessage;
  const canSubmit = subject.trim() && initialMessage.trim() && !isPending;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSubject("");
        setInitialMessage("");
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !enduserId) return;

    try {
      const createdRoom = await createRoomMutation.mutateAsync({
        title: subject.trim(),
        enduserIds: [enduserId],
      });

      await sendMessageAsync({
        roomId: createdRoom.id,
        message: initialMessage.trim(),
      });

      setSubject("");
      setInitialMessage("");
      onOpenChange(false);
      onConversationCreated();
    } catch {
      // Errors handled by mutation state
    }
  }, [
    canSubmit,
    enduserId,
    subject,
    initialMessage,
    createRoomMutation,
    sendMessageAsync,
    onOpenChange,
    onConversationCreated,
  ]);

  return {
    t,
    subject,
    setSubject,
    initialMessage,
    setInitialMessage,
    isPending,
    canSubmit,
    handleOpenChange,
    handleSubmit,
  };
};
